import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Ban, Building2, Plus, Store, Users } from 'lucide-react'
import {
  useVendors,
  useCreateVendor,
  useUpdateVendor,
  useVendorVenues,
  useLinkVendorVenue,
  useUnlinkVendorVenue,
  useSetVendorServices,
  useDeactivateVendor,
} from '@/hooks/useVendors'
import { VendorRemovalDialog } from '@/components/VendorRemovalDialog'
import { VendorServicesPicker } from '@/components/VendorServicesPicker'
import { requestErrorMessage } from '@/helpers/apiClient'
import { createVendorInputSchema, updateVendorInputSchema } from '@lib/input/vendor.input'
import type { VendorDto, VendorVenueDto } from '@lib/dto/vendor.dto'
import { Button } from '@/components/ui/button'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { SectionNav, type PageSection } from '@/components/ui/section-nav'
import { VendorPeople } from '@/components/VendorPeople'

/** Details section: rename the vendor, committing on blur. Address is the
 * vendor's own concern (edited on their `/vendor` page), so it isn't shown here. */
function DetailsSection({ vendor }: { vendor: VendorDto }) {
  const update = useUpdateVendor()
  const setServices = useSetVendorServices()
  const [name, setName] = React.useState(vendor.name)
  const [error, setError] = React.useState<string | null>(null)

  function commit(): void {
    setError(null)
    const trimmed = name.trim()
    if (trimmed === vendor.name) return
    const parsed = updateVendorInputSchema.safeParse({ name: trimmed })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid input')
      return
    }
    update.mutate({ id: vendor.id, input: parsed.data }, { onError: (e) => setError(requestErrorMessage(e)) })
  }

  return (
    <div className="space-y-6">
      <Field label="Business name" htmlFor="vendor-name" error={error} hint="Its users manage the vendor's own address.">
        <Input
          id="vendor-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={commit}
          className="max-w-md"
          autoFocus
        />
      </Field>
      <div className="space-y-2">
        <p className="text-sm font-medium text-ink">Services offered</p>
        <p className="text-sm text-ink-muted">
          Normally the vendor sets these; edit here only to help. Offering Furniture unlocks their library.
        </p>
        <VendorServicesPicker
          value={vendor.services}
          busy={setServices.isPending}
          onSave={async (services) => {
            await setServices.mutateAsync({ id: vendor.id, services })
          }}
        />
      </div>
    </div>
  )
}

/** One venue row with a link/unlink toggle. */
function VenueLinkRow({
  vendorId,
  venue,
}: {
  vendorId: string
  venue: VendorVenueDto
}) {
  const link = useLinkVendorVenue(vendorId)
  const unlink = useUnlinkVendorVenue(vendorId)
  const busy = link.isPending || unlink.isPending
  const [confirming, setConfirming] = React.useState(false)

  function toggle(): void {
    if (venue.linked) setConfirming(true)
    else link.mutate({ venueId: venue.id })
  }

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Building2 className="h-4 w-4 shrink-0 text-ink-muted" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{venue.name}</p>
        <p className="truncate text-xs text-ink-muted">
          {venue.tenantName}
          {venue.address ? ` · ${venue.address}` : ''}
        </p>
      </div>
      <Button pending={busy} variant={venue.linked ? 'outline' : 'default'} size="sm" onClick={toggle}>
        {venue.linked ? 'Linked' : 'Link'}
      </Button>
      <VendorRemovalDialog
        open={confirming}
        onOpenChange={(open) => {
          if (open) unlink.reset()
          setConfirming(open)
        }}
        target={{ kind: 'unlink', vendorId, venueId: venue.id }}
        title={`Unlink from ${venue.name}?`}
        lead="The vendor stops seeing this venue, and the venue’s approvals of it are withdrawn."
        confirmLabel="Unlink"
        busy={unlink.isPending}
        error={unlink.error ? requestErrorMessage(unlink.error) : null}
        onConfirm={() => unlink.mutate({ venueId: venue.id }, { onSuccess: () => setConfirming(false) })}
      />
    </li>
  )
}

/**
 * Deactivate the company (docs/planner-vendors.md, decision 39). There is no
 * delete: a company that has been on an event keeps its name on it, so it is
 * only ever switched off.
 */
function DeactivateSection({ vendor }: { vendor: VendorDto }) {
  const deactivate = useDeactivateVendor()
  const [confirming, setConfirming] = React.useState(false)

  if (vendor.deactivatedAt !== null) {
    return (
      <p className="rounded-md border border-line px-4 py-3 text-sm text-ink-muted">
        Deactivated on {new Date(vendor.deactivatedAt).toLocaleDateString('en-US', { dateStyle: 'long' })}. Its people
        cannot sign in, it is not in search, and no venue can use it. Its name stays on the events it was on.
      </p>
    )
  }
  return (
    <>
      <Button
        variant="outline"
        className="text-danger-ink hover:bg-danger-soft hover:text-danger-ink"
        onClick={() => {
          deactivate.reset()
          setConfirming(true)
        }}
      >
        <Ban className="h-4 w-4" /> Deactivate {vendor.name}
      </Button>
      <VendorRemovalDialog
        open={confirming}
        onOpenChange={setConfirming}
        target={{ kind: 'deactivate', vendorId: vendor.id }}
        title={`Deactivate ${vendor.name}?`}
        lead="Its people lose access at once, it leaves search, and no venue can use it. It is kept, with its name on the events it was on."
        confirmLabel="Deactivate"
        busy={deactivate.isPending}
        error={deactivate.error ? requestErrorMessage(deactivate.error) : null}
        onConfirm={() => deactivate.mutate({ vendorId: vendor.id }, { onSuccess: () => setConfirming(false) })}
      />
    </>
  )
}

/** Venues section: link/unlink the vendor to venues across all tenants. */
function VenuesSection({ vendorId }: { vendorId: string }) {
  const venuesQuery = useVendorVenues(vendorId)

  if (venuesQuery.isPending) {
    return (
      <LoadingState label="Loading venues…" />
    )
  }
  if (venuesQuery.isError) {
    return (
      <ErrorState message={requestErrorMessage(venuesQuery.error)} onRetry={() => void venuesQuery.refetch()} />
    )
  }
  if (venuesQuery.data.venues.length === 0) {
    return <p className="py-8 text-center text-sm text-ink-muted">There are no venues to link yet.</p>
  }
  return (
    <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
      {venuesQuery.data.venues.map((v) => (
        <VenueLinkRow key={v.id} vendorId={vendorId} venue={v} />
      ))}
    </ul>
  )
}

/** Create form for a new vendor (name only; users/venues are managed after). */
function CreateVendor() {
  const navigate = useNavigate()
  const createVendor = useCreateVendor()
  const [name, setName] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    const parsed = createVendorInputSchema.safeParse({ name })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid input')
      return
    }
    try {
      const created = await createVendor.mutateAsync(parsed.data)
      // Continue to the vendor's page to invite users and link venues.
      navigate(`/admin/vendors/${created.id}`)
    } catch (e) {
      setError(requestErrorMessage(e))
    }
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit} noValidate>
      <Field label="Vendor name" htmlFor="vendor-name" error={error} hint="Its users are invited afterward, from the Users tab.">
        <Input id="vendor-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Rentals" autoFocus />
      </Field>
      <FormActions>
        <Button type="button" variant="outline" onClick={() => navigate('/admin/vendors')}>
          Cancel
        </Button>
        <Button pending={createVendor.isPending} type="submit">
          <Plus className="h-4 w-4" />
          {createVendor.isPending ? 'Adding…' : 'Add vendor'}
        </Button>
      </FormActions>
    </form>
  )
}

/**
 * Super-admin create/edit page for a vendor. Create is a single name form; edit
 * is a sectioned page (Details / Users / Venues) navigated by a left subnav.
 */
export function VendorAdminPage() {
  const { vendorId } = useParams<{ vendorId: string }>()
  const isEdit = vendorId !== undefined
  const navigate = useNavigate()
  const vendorsQuery = useVendors()

  const existing: VendorDto | undefined = isEdit ? vendorsQuery.data?.vendors.find((v) => v.id === vendorId) : undefined

  const backLink = (
    <button
      type="button"
      onClick={() => navigate('/admin/vendors')}
      className="inline-flex items-center gap-1.5 text-sm text-ink-muted transition-colors hover:text-ink"
    >
      <ArrowLeft className="h-4 w-4" /> Vendors
    </button>
  )

  if (!isEdit) {
    return (
      <PageContainer className="space-y-6">
        <div className="space-y-3">
          {backLink}
          <PageHeader
            title="New vendor"
            description="Add a supplier. You can invite its users and link venues next."
          />
        </div>
        <CreateVendor />
      </PageContainer>
    )
  }

  if (vendorsQuery.isPending) return <LoadingState label="Loading vendor…" />
  if (vendorsQuery.isError) {
    return <ErrorState message={requestErrorMessage(vendorsQuery.error)} onRetry={() => void vendorsQuery.refetch()} />
  }
  if (!existing) {
    return <ErrorState message="That vendor no longer exists." onRetry={() => navigate('/admin/vendors')} />
  }

  const sections: PageSection[] = [
    { id: 'details', label: 'Details', icon: Store, description: 'Rename the vendor.', content: <DetailsSection vendor={existing} /> },
    {
      id: 'users',
      label: 'Users',
      icon: Users,
      description: 'Invite people to manage this vendor and control their access.',
      content: <VendorPeople scope={{ kind: 'vendor', vendorId: existing.id }} />,
    },
    {
      id: 'venues',
      label: 'Venues',
      icon: Building2,
      description: "Link this vendor to venues — its furniture becomes available in a linked venue's events.",
      content: <VenuesSection vendorId={existing.id} />,
    },
    {
      id: 'deactivate',
      label: 'Deactivate',
      icon: Ban,
      description: 'Stop the company working anywhere. A company is never deleted.',
      content: <DeactivateSection vendor={existing} />,
    },
  ]

  return (
    <PageContainer width="wide" className="space-y-6">
      <div className="space-y-3">
        {backLink}
        <h1 className="text-2xl font-semibold tracking-tight">{existing.name}</h1>
        <p className="text-sm text-ink-muted">
          {existing.userCount} {existing.userCount === 1 ? 'user' : 'users'}
          {existing.deactivatedAt !== null ? ' · Deactivated' : ''}
        </p>
      </div>
      <SectionNav sections={sections} ariaLabel="Vendor settings" />
    </PageContainer>
  )
}
